import { combine, Observable } from "../observable.js";
import "./select.css";

class SelectElement extends HTMLElement {
    static observedAttributes = [
        "placeholder",
        "name",
        "multiple",
        "required",
        "variant",
    ];

    $attributes = new Observable<{
        placeholder: string;
        multiple: boolean;
        required: boolean;
        variant: string;
        name?: string;
    }>({
        placeholder: "",
        multiple: false,
        required: false,
        variant: "bd-surface-10",
    });

    $search = new Observable("");

    #unsubs: Function[] = [];

    constructor() {
        super();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            switch (name) {
                case "placeholder":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        placeholder: newValue,
                    }));
                    break;
                case "name":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        name: newValue,
                    }));
                    break;
                case "value":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        value: newValue,
                    }));
                    break;
                case "multiple":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        multiple: newValue === "" || newValue === "true",
                    }));
                    break;
                case "required":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        required: newValue === "" || newValue === "true",
                    }));
                    break;
                case "variant":
                    this.$attributes.broadcast((prev) => ({
                        ...prev,
                        variant: newValue,
                    }));
            }
        }
    }

    connectedCallback() {
        const options: HTMLOptionElement[] = [];
        for (const childNode of this.childNodes) {
            if (childNode instanceof HTMLOptionElement) {
                options.push(childNode);
            }
        }

        this.innerHTML = "";

        const popover = document.createElement("div");
        popover.className = "bd-popover bottom-center";
        popover.popover = "auto";

        const popoverCard = document.createElement("div");
        popoverCard.className = "bd-card";
        popover.appendChild(popoverCard);

        const popoverCardBody = document.createElement("div")
        popoverCardBody.className = "bd-card-body"
        popoverCard.appendChild(popoverCardBody)

        const triggerButton = document.createElement("button");
        // triggerButton.className = "bd-input bd-select-trigger";
        triggerButton.type = "button";
        triggerButton.popoverTargetElement = popover;
        triggerButton.popoverTargetAction = "toggle";

        const emptyCheck = document.createElement("input");
        emptyCheck.type = "hidden";
        emptyCheck.value = "";

        this.appendChild(triggerButton);
        this.appendChild(popover);

        const $selectedOptions = new Observable<HTMLOptionElement[]>([]);

        this.#unsubs.push(
            $selectedOptions.subscribe((options) => {
                if (options.length < 1) {
                    triggerButton.innerHTML = `<span>${this.$attributes.data.placeholder}</span><bd-icon value="nav-arrow-down"></bd-icon>`;
                    popoverCardBody.appendChild(emptyCheck);
                } else {
                    triggerButton.innerHTML = `<div class="bd-select-selection"><span>${options[0].innerText}</span>${options.length > 1 ? `<span>+${options.length - 1}</span>` : ""}</div><bd-icon value="nav-arrow-down"></bd-icon>`;
                    popoverCardBody.removeChild(emptyCheck);
                }
            }),
        );

        this.#unsubs.push(
            combine(this.$attributes, this.$search).subscribe(
                ([{ name, multiple, required, variant }, search]) => {
                    popoverCardBody.innerHTML = "";

                    // set variants
                    triggerButton.className = "bd-input bd-select-trigger";
                    if (variant) {
                        triggerButton.classList.add(variant);
                    }
                    if (variant) {
                        popoverCard.classList.add(variant);
                    }

                    const checkedOptions = options.flatMap((option) => {
                        if (!option.innerText.includes(search)) {
                            return [];
                        }
                        const container = document.createElement("label");
                        container.className = "bd-select-option";

                        const check = document.createElement("input");
                        check.className = "bd-input";
                        check.value = option.value;
                        check.checked = option.selected;

                        check.onchange = (event) => {
                            const evCheck = event.target as HTMLInputElement;
                            if (multiple) {
                                $selectedOptions.broadcast((prev) => {
                                    if (!evCheck.checked) {
                                        return prev.filter(
                                            (i) => i.value !== option.value,
                                        );
                                    } else {
                                        return prev.concat(option);
                                    }
                                });
                            } else {
                                $selectedOptions.broadcast((prev) =>
                                    evCheck.checked ? [option] : prev,
                                );
                            }
                        };
                        if (required) {
                            check.required = true;
                        }

                        if (name) {
                            emptyCheck.name = name;
                            check.name = name;
                        }
                        if (multiple) {
                            check.type = "checkbox";
                        } else {
                            check.type = "radio";
                        }

                        const label = document.createElement("span");
                        label.innerText = option.innerText;

                        container.appendChild(check);
                        container.appendChild(label);

                        popoverCardBody.appendChild(container);

                        if (check.checked) {
                            return option;
                        }

                        return [];
                    });

                    $selectedOptions.broadcast(checkedOptions);
                },
                true,
            ),
        );
    }

    disconnectedCallback() {
        this.#unsubs.forEach((unsub) => {
            unsub();
        });
        this.#unsubs = [];
    }
}

customElements.define("bd-select", SelectElement);
